import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mic, Users } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Card, CardContent } from "@/shared/ui/card";
import { PageHeader } from "@/shared/ui/PageHeader";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { useStaggerReveal } from "@/shared/motion/useStaggerReveal";
import { cn } from "@/shared/lib/utils";
import {
  CARD_WALL,
  CARD_WALL_MEDIA,
  CARD_WALL_RADIUS,
  clickableCardProps,
} from "@/shared/ui/cardWall";
import { characterService, type CharacterDto } from "@/entities/character/api/characterService";
import { CharacterThumb } from "@/entities/character/ui/CharacterThumb";
import { MediaCardActionRow } from "@/entities/media-asset/ui/MediaCardActionRow";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { CHARACTER_DELETE_DESCRIPTION } from "../lib/characterDelete";
import { CharacterCreateEntry } from "./CharacterCreateEntry";
import { errorText } from "@/shared/lib/apiErrors";

/**
 * 人物库墙(packs W2 T2)。一等库:主素材卡面 + 名字 + 类型徽标 + 外观一句话 + 素材数。
 * 0916:顶上加**真人 / 虚拟人**两档筛选(老板拍板;归一后这是人物层唯一还成立的分类维度),
 * 卡面按主素材 kind 分派 img/video(「只有视频」的人物此前卡面是裂图)。
 * 建人物**单入口**(0915 归一批 A):一颗「新建人物」→ 选类型 → 真人走采集流 / 虚拟人开表单。
 * 卡墙走 media 档(150~260):卡面主体就是一张定妆图,浏览量大于阅读量 ——
 * 正是 cardWall 头注里 media 档的判据;此前套 cap 档(250~365)把定妆图放到
 * 场景卡量级,一行只有 3~4 张(2026-09-09 老板「图片显示太大」)。
 */
export default function CharacterLibraryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const listQuery = useQuery({
    queryKey: ["characters"],
    queryFn: () => characterService.list(),
  });
  const all = listQuery.data ?? [];
  /**
   * 人物类型筛选(老板 0916「我们的人物库就分两种筛选,真人,或者虚拟人物」)。
   *
   * ★这是归一之后唯一还成立的分类维度:素材类型(照片/视频)在人物这一层没有意义
   * (同一个人两样都有),能力也不再分家(虚拟人同样能驱动、同样走 vapeur、同样进素材库引用)。
   * 真人/虚拟的差别只剩一条 —— 真人要肖像授权存证。
   */
  const [typeFilter, setTypeFilter] = useState<"all" | "real" | "virtual">("all");
  const counts = {
    all: all.length,
    real: all.filter((c) => c.isRealPerson).length,
    virtual: all.filter((c) => !c.isRealPerson).length,
  };
  const items =
    typeFilter === "all" ? all : all.filter((c) => c.isRealPerson === (typeFilter === "real"));
  /**
   * 库墙直接删(1015:此前删除只藏在详情页「更多操作」里,墙上找不到)。
   * ★缓存处理同详情页删除(TOP#9):先摘掉 ["characters", id] 前缀(详情/物料),列表只 exact 刷 ——
   *   前缀 invalidate 会把挂着的详情查询标脏去重拉刚删掉的人物 → 404 连弹。
   */
  const removeMut = useMutationWithToast({
    mutationFn: (c: CharacterDto) => characterService.remove(c.id),
    successToast: "已删除人物",
    errorToast: "删除失败",
    onSuccess: (_d, c) => {
      queryClient.removeQueries({ queryKey: ["characters", c.id] });
      queryClient.invalidateQueries({ queryKey: ["characters"], exact: true });
    },
  });
  const handleDelete = async (c: CharacterDto) => {
    const ok = await confirm({
      title: `删除人物「${c.name}」?`,
      description: CHARACTER_DELETE_DESCRIPTION,
      confirmText: "删除",
      variant: "destructive",
    });
    if (ok) removeMut.mutate(c);
  };
  const gridRef = useRef<HTMLDivElement>(null);
  useStaggerReveal(gridRef, { deps: [items.length] });

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/ai-digital-human"
        icon={Users}
        title="人物库"
        subtitle="团队共用的人物资产:定妆/表情/姿态/造型物料一次建好,视频、海报、分身按槽取用。"
        actions={
          <div className="flex gap-2">
            {/* ★0916 拆掉这里的「素材库」按钮:归一后 `/ai-digital-human/avatars` 已经
                重定向到本页 —— 这颗按钮点下去是原地打转(0915 发版后就是这个状态)。
                素材现在长在人物名下,人物库就是素材库;那条路由只留着接老书签。
                声音库不同,它是真的另一页(声纹可跨人物、也能独立跑 TTS),保留直达。 */}
            <Button variant="outline" onClick={() => navigate("/ai-digital-human/voices")}>
              <Mic className="mr-2 h-4 w-4" />
              声音库
            </Button>
            {/* ★0915 归一批 A:两颗建人物按钮合并成一颗;0916 两条路都走建档流 —— 见 CharacterCreateEntry 头注。 */}
            <CharacterCreateEntry />
          </div>
        }
      />

      <QueryErrorBanner
        isError={listQuery.isError}
        message={listQuery.error ? errorText(listQuery.error) : undefined}
        onRetry={() => listQuery.refetch()}
      />

      {/* 筛选条:人物 ≥1 个才出 —— 空库上给一排 0/0/0 的 chips 是纯噪声。 */}
      {!listQuery.isLoading && !listQuery.isError && all.length > 0 && (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="按人物类型筛选">
          {(
            [
              { key: "all", label: "全部", n: counts.all },
              { key: "real", label: "真人", n: counts.real },
              { key: "virtual", label: "虚拟人", n: counts.virtual },
            ] as const
          ).map((c) => (
            <Button
              key={c.key}
              type="button"
              size="sm"
              variant={typeFilter === c.key ? "default" : "outline"}
              aria-pressed={typeFilter === c.key}
              data-testid={`character-filter-${c.key}`}
              className="h-7 rounded-full px-3 text-xs"
              onClick={() => setTypeFilter(c.key)}
            >
              {c.label}
              <span className="ml-1.5 tabular-nums opacity-70">{c.n}</span>
            </Button>
          ))}
        </div>
      )}

      {listQuery.isLoading ? (
        <div className={CARD_WALL}>
          <div className={CARD_WALL_MEDIA} data-testid="character-wall-skeleton">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className={cn("fx-skeleton border border-border/50 bg-card/40", CARD_WALL_RADIUS)}
                // 骨架高度 = 3:4 定妆图 + 三行文案区,按最终卡形状给
                style={{ aspectRatio: "3 / 5" }}
              />
            ))}
          </div>
        </div>
      ) : listQuery.isError ? null : all.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center text-sm text-muted-foreground">
            <p>还没有人物。真人和虚拟人都能建 —— 建完用法一样,照片视频都能传。</p>
            <CharacterCreateEntry />
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center text-sm text-muted-foreground">
            <p>没有{typeFilter === "real" ? "真人" : "虚拟"}人物。</p>
            <Button variant="outline" size="sm" onClick={() => setTypeFilter("all")}>
              看全部
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className={CARD_WALL}>
          <div ref={gridRef} className={CARD_WALL_MEDIA}>
            {items.map((c) => (
              <CharacterCard
                key={c.id}
                character={c}
                onOpen={() => navigate(`/characters/${c.id}`)}
                onDelete={() => void handleDelete(c)}
                deleting={removeMut.isPending && removeMut.variables?.id === c.id}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CharacterCard({
  character: c,
  onOpen,
  onDelete,
  deleting,
}: {
  character: CharacterDto;
  onOpen: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  // ★0916:卡面一句话从 persona「风格·气质」改成**外观** —— persona 已退役,
  //   而且卡面显示外观本来就更有用(它是真进提示词链的那一句)。
  const oneLiner = c.appearance ?? "";
  return (
    <Card
      data-character-card={c.id}
      className={cn(
        "fx-hover-lift flex cursor-pointer flex-col overflow-hidden",
        CARD_WALL_RADIUS,
        c.status === "disabled" && "opacity-60"
      )}
      {...clickableCardProps(onOpen)}
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted">
        {/* 缩略图优先、原图兜底:库墙一屏几十张,缩略图收益最大。
            ★0908 用户「分辨率不同显示会乱」:此前 object-cover 硬裁 —— 横图切两边、
              长图切头脚,人脸经常被切没。换 WorkCard 同款方案(它的头注就是为
              「产物比例天差地别」写的):定框不变,模糊铺底吃掉留白,前景 contain 整图可见。 */}
        {/* 主素材可能是视频(只有视频的人物);三处消费面共用件按 kind 分派,别再各写 ?? 链。 */}
        <CharacterThumb
          character={c}
          fit="contain"
          iconClassName="h-10 w-10"
          emptyHint="还没有素材"
        />
      </div>
      {/* media 档卡窄(150~260),p-4 会把文字区挤成两指宽 —— 收到 p-3 同 WorkCard 密度。 */}
      <CardContent className="flex flex-1 flex-col gap-1.5 p-3">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{c.name}</span>
          {/* 两类都挂标:筛选条一开,「这张是哪一类」必须在卡上读得出来
              (只给真人挂标时,虚拟人靠"没有标"来识别 —— 那是靠缺席传达信息)。 */}
          <Badge variant="secondary" className="shrink-0 text-2xs">
            {c.isRealPerson ? "真人" : "虚拟人"}
          </Badge>
          {c.status === "disabled" && (
            <Badge variant="outline" className="shrink-0 text-2xs">
              停用
            </Badge>
          )}
        </div>
        {oneLiner && <p className="line-clamp-1 text-xs text-muted-foreground">{oneLiner}</p>}
        <p className="mt-auto pt-1 text-xs text-muted-foreground/80">
          {c.materialCount ?? 0} 份素材
        </p>
        {/* 共用操作行(作品卡同款):只给删除,改名/启停在详情页。行内按钮已 stopPropagation,不会连带进详情。 */}
        {/* 卡壳的 clickableCardProps 收 Enter/空格 → 键盘在删除钮上回车会冒泡成「打开详情」,这里截住。 */}
        <div role="presentation" onKeyDown={(e) => e.stopPropagation()}>
          <MediaCardActionRow onDelete={onDelete} deleting={deleting} />
        </div>
      </CardContent>
    </Card>
  );
}
